import { useMemo } from 'react';
import {
  Activity,
  Bell,
  DollarSign,
  Image as ImageIcon,
  MapPin,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/services/userService';
import { trackingService } from '@/features/location-tracking/services/trackingService';
import type { User, UserListResponse } from '@/types/user';
import type { UserActivityItem } from '@/features/location-tracking/types/tracking';
import { PROGRAM_REGISTRY, type ProgramRegistryEntry } from '@/config/programs';

const COUNT_QUERY_STALE_MS = 60 * 1000;
// Live tracking data changes constantly — a shorter cache keeps the
// tile close-to-real-time without hammering the endpoint if the
// dashboard is re-mounted during navigation.
const LIVE_QUERY_STALE_MS = 30 * 1000;
const RECENT_USERS_LIMIT = 5;

/**
 * Fetch just the `total` for a filter by asking for a single row.
 * The list endpoint always echoes the full total in the envelope,
 * so `page_size: 1` is the cheapest way to get a count without a
 * dedicated /count route on the backend.
 */
const fetchUserCount = async (status?: 'Active' | 'Inactive'): Promise<number> => {
  const response = await userService.getUsers({ page: 1, page_size: 1, status });
  return response.total ?? 0;
};

const fetchRecentUsers = (): Promise<UserListResponse> =>
  userService.getUsers({ page: 1, page_size: RECENT_USERS_LIMIT });

/** YYYY-MM-DD in the viewer's local timezone. */
const todayIso = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const initials = (u: User): string => {
  const first = (u.first_name || '').trim()[0] || '';
  const last = (u.last_name || '').trim()[0] || '';
  const seed = `${first}${last}`.trim();
  if (seed) return seed.toUpperCase();
  const emailSeed = (u.email || '').trim()[0];
  return (emailSeed || '?').toUpperCase();
};

const displayName = (u: User): string => {
  const name = [u.first_name, u.middle_name, u.last_name]
    .filter((part) => part && part.trim())
    .join(' ')
    .trim();
  return name || u.email || 'Unnamed user';
};

/**
 * True when the roles array grants access to the location-tracking
 * activity endpoint. ADMIN sees everything; otherwise any of the
 * three location roles is enough — the endpoint itself further
 * filters what a caller can see.
 */
const canReadLiveTracking = (roles: string[] | undefined): boolean => {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  if (roles.includes('ADMIN')) return true;
  return roles.some((r) => r.startsWith('LOCATION.'));
};

/**
 * True when the roles array can read `/console/user_management/list`.
 * The backend gates that endpoint on ADMIN — non-ADMIN callers get
 * 403 — so we skip the four user-count / recent-users queries on
 * this dashboard for anyone who isn't ADMIN. Without this gate a
 * standard reviewer would see every user tile stuck on Retry.
 */
const canReadUsers = (roles: string[] | undefined): boolean =>
  Array.isArray(roles) && roles.includes('ADMIN');

/**
 * Any-of role helpers for Quick Actions link filtering. Each link
 * navigates into a route that will itself 403 for a viewer without
 * the right role, so a static allow-list here just keeps the
 * dashboard from advertising links the viewer can't use.
 */
const hasAnyRole = (
  roles: string[] | undefined,
  needle: (r: string) => boolean,
): boolean =>
  Array.isArray(roles) && (roles.includes('ADMIN') || roles.some(needle));

const canOpenOnboarding = (roles: string[] | undefined): boolean =>
  hasAnyRole(roles, (r) => r.startsWith('ONBOARDING.'));

const canOpenHonorarium = (roles: string[] | undefined): boolean =>
  hasAnyRole(roles, (r) => r.startsWith('INVOICE.'));

const canOpenAssets = (roles: string[] | undefined): boolean =>
  hasAnyRole(
    roles,
    (r) =>
      r.startsWith('IMPACT_ASSET.') ||
      r.startsWith('ICONIK.') ||
      r.startsWith('ASSET_LIBRARY.') ||
      r.startsWith('GALLERY.'),
  );

const canOpenNotifications = (roles: string[] | undefined): boolean =>
  hasAnyRole(roles, (r) => r === 'NOTIFICATION.CREATOR');

/**
 * Group today's activity items by program. A user tagged with N
 * programs contributes to each — the tile shows program-level
 * check-in reach, not distinct-headcount across all programs
 * (which would just equal the "Live now" tile most days). A user
 * counts once per program even if they have multiple sessions;
 * `is_live: true` also implies the user has at least one check-in.
 */
const groupCheckinsByProgram = (
  items: UserActivityItem[],
): Array<{ program: ProgramRegistryEntry; total: number; live: number }> => {
  const totals = new Map<string, { total: number; live: number }>();
  for (const item of items) {
    const programCodes = item.program_id ?? [];
    if (programCodes.length === 0) continue;
    // A user with the same program listed twice shouldn't be
    // counted twice under it. Dedup per-item.
    const seen = new Set<string>();
    for (const code of programCodes) {
      const norm = code.toUpperCase();
      if (seen.has(norm)) continue;
      seen.add(norm);
      const bucket = totals.get(norm) ?? { total: 0, live: 0 };
      bucket.total += 1;
      if (item.is_live) bucket.live += 1;
      totals.set(norm, bucket);
    }
  }

  // Roll each ProgramCode up to its ProgramRegistryEntry — Sports
  // groups 5 codes into one label, so RGSH + SD + RGSF etc. all
  // land under the same row. Presented in registry order for
  // stable ordering across renders.
  return PROGRAM_REGISTRY.map((program) => {
    let total = 0;
    let live = 0;
    for (const code of program.codes) {
      const bucket = totals.get(code.toUpperCase());
      if (!bucket) continue;
      total += bucket.total;
      live += bucket.live;
    }
    return { program, total, live };
  }).filter((row) => row.total > 0);
};

interface StatTileProps {
  name: string;
  value: number | undefined;
  icon: typeof Users;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  hint?: string;
}

const StatTile = ({ name, value, icon: Icon, isLoading, error, onRetry, hint }: StatTileProps) => (
  <div className="card">
    <div className="card-body">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="p-3 bg-rgf-primary-100 rounded-lg">
            <Icon className="h-6 w-6 text-rgf-primary-600" />
          </div>
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-rgf-gray-500 truncate">{name}</dt>
            <dd className="mt-1">
              {isLoading ? (
                <div
                  className="h-8 w-20 rounded bg-rgf-gray-100 animate-pulse"
                  aria-label="Loading"
                />
              ) : error ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-rgf-gray-400">—</span>
                  <button
                    type="button"
                    onClick={onRetry}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <span className="text-2xl font-semibold text-rgf-gray-900">
                  {(value ?? 0).toLocaleString()}
                </span>
              )}
            </dd>
            {hint && !isLoading && !error && (
              <dd className="mt-0.5 text-xs text-rgf-gray-500 truncate">{hint}</dd>
            )}
          </dl>
        </div>
      </div>
    </div>
  </div>
);

export const Dashboard = () => {
  const { user } = useAuth();
  const canSeeUsers = canReadUsers(user?.roles);
  const canSeeTracking = canReadLiveTracking(user?.roles);
  const today = useMemo(() => todayIso(), []);

  // User-count / recent-users queries are ADMIN-only endpoints —
  // gate them with `enabled` so a non-ADMIN viewer never fires the
  // request. React Query keeps disabled queries at `isLoading:
  // false` with `data: undefined`, which is fine because the
  // corresponding tiles are hidden entirely below.
  const totalQuery = useQuery({
    queryKey: ['dashboard', 'users', 'count', 'all'],
    queryFn: () => fetchUserCount(),
    enabled: canSeeUsers,
    staleTime: COUNT_QUERY_STALE_MS,
  });
  const activeQuery = useQuery({
    queryKey: ['dashboard', 'users', 'count', 'active'],
    queryFn: () => fetchUserCount('Active'),
    enabled: canSeeUsers,
    staleTime: COUNT_QUERY_STALE_MS,
  });
  const inactiveQuery = useQuery({
    queryKey: ['dashboard', 'users', 'count', 'inactive'],
    queryFn: () => fetchUserCount('Inactive'),
    enabled: canSeeUsers,
    staleTime: COUNT_QUERY_STALE_MS,
  });

  const recentQuery = useQuery({
    queryKey: ['dashboard', 'users', 'recent'],
    queryFn: fetchRecentUsers,
    enabled: canSeeUsers,
    staleTime: COUNT_QUERY_STALE_MS,
  });

  // Single activity fetch powers both the "Live now" tile and the
  // per-program check-ins card. `getUserActivity` walks all pages
  // itself, so we get everyone for `today` in one query slot.
  const activityQuery = useQuery({
    queryKey: ['dashboard', 'live-activity', today],
    queryFn: () => trackingService.getUserActivity(today),
    enabled: canSeeTracking,
    staleTime: LIVE_QUERY_STALE_MS,
  });

  const activityItems = activityQuery.data ?? [];
  const liveNow = useMemo(
    () => activityItems.filter((u) => u.is_live).length,
    [activityItems],
  );
  const programBreakdown = useMemo(
    () => groupCheckinsByProgram(activityItems),
    [activityItems],
  );
  const maxProgramTotal = programBreakdown.reduce((max, row) => Math.max(max, row.total), 0);

  const recentUsers = recentQuery.data?.list ?? [];

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Welcome back, {user?.first_name || user?.email}! Here's what's happening with your organization.
          </p>
        </div>
      </div>

      {/* Tile row — count the visible tiles so the grid doesn't leave
          empty columns on wider screens (e.g. Live-only reviewer sees
          a single centered card instead of a 4-col grid with three
          holes). Only render the row at all when there is something
          to show. */}
      {(canSeeUsers || canSeeTracking) && (
        <div className={tileGridClass([canSeeUsers, canSeeUsers, canSeeUsers, canSeeTracking].filter(Boolean).length)}>
          {canSeeUsers && (
            <>
              <StatTile
                name="Total Users"
                value={totalQuery.data}
                icon={Users}
                isLoading={totalQuery.isLoading}
                error={totalQuery.error}
                onRetry={() => totalQuery.refetch()}
              />
              <StatTile
                name="Active Users"
                value={activeQuery.data}
                icon={UserCheck}
                isLoading={activeQuery.isLoading}
                error={activeQuery.error}
                onRetry={() => activeQuery.refetch()}
              />
              <StatTile
                name="Inactive Users"
                value={inactiveQuery.data}
                icon={UserX}
                isLoading={inactiveQuery.isLoading}
                error={inactiveQuery.error}
                onRetry={() => inactiveQuery.refetch()}
              />
            </>
          )}
          {canSeeTracking && (
            <StatTile
              name="Live on Tracking"
              value={liveNow}
              icon={MapPin}
              isLoading={activityQuery.isLoading}
              error={activityQuery.error}
              onRetry={() => activityQuery.refetch()}
              hint={
                activityQuery.isLoading || activityQuery.error
                  ? undefined
                  : `${activityItems.length.toLocaleString()} checked in today`
              }
            />
          )}
        </div>
      )}

      {/* Card row — same adaptive-cols pattern as tiles. Quick
          Actions is always rendered (it's static links, no API), so
          the row is never empty for a logged-in user. */}
      <div className={cardGridClass([canSeeUsers, canSeeTracking, true].filter(Boolean).length)}>
        {canSeeUsers && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-rgf-gray-900">Recent Users</h3>
          </div>
          <div className="card-body p-0">
            <div className="overflow-hidden">
              {recentQuery.isLoading ? (
                <ul className="divide-y divide-rgf-gray-100" aria-label="Loading recent users">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <li key={i} className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 rounded-full bg-rgf-gray-100 animate-pulse" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-2/3 rounded bg-rgf-gray-100 animate-pulse" />
                          <div className="h-3 w-1/2 rounded bg-rgf-gray-100 animate-pulse" />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : recentQuery.error ? (
                <div className="px-6 py-10 text-center space-y-3">
                  <p className="text-sm text-rgf-gray-600">
                    {(recentQuery.error as Error)?.message || 'Failed to load recent users.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => recentQuery.refetch()}
                    className="text-sm font-medium text-rgf-primary-600 hover:text-rgf-primary-500"
                  >
                    Try again
                  </button>
                </div>
              ) : recentUsers.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-rgf-gray-500">
                  No users yet.
                </p>
              ) : (
                <ul className="divide-y divide-rgf-gray-100">
                  {recentUsers.map((u) => (
                    <li key={u.db_id ?? u.fusion_auth_user_id ?? u.email} className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-rgf-primary-700">
                            {initials(u)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-rgf-gray-900 truncate">
                            {displayName(u)}
                          </p>
                          <p className="text-sm text-rgf-gray-500 truncate">{u.email}</p>
                        </div>
                        <div className="flex flex-col items-end">
                          <span
                            className={`badge ${
                              u.status === 'Active' ? 'badge-success' : 'badge-error'
                            }`}
                          >
                            {u.status}
                          </span>
                          {u.roles?.[0] && (
                            <span
                              className="text-xs text-rgf-gray-500 mt-1 truncate max-w-[10rem]"
                              title={u.roles.join(', ')}
                            >
                              {u.roles[0]}
                              {u.roles.length > 1 ? ` +${u.roles.length - 1}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-100">
              <Link
                to="/users"
                className="text-sm font-medium text-rgf-primary-600 hover:text-rgf-primary-500"
              >
                View all users →
              </Link>
            </div>
          </div>
        </div>
        )}

        {canSeeTracking && (
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="text-lg font-medium text-rgf-gray-900">Check-ins Today by Program</h3>
              <span className="text-xs text-rgf-gray-500">Live badges = currently on tracking</span>
            </div>
            <div className="card-body p-0">
              {activityQuery.isLoading ? (
                <ul className="divide-y divide-rgf-gray-100" aria-label="Loading check-ins">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li key={i} className="px-6 py-4 space-y-2">
                      <div className="h-4 w-1/3 rounded bg-rgf-gray-100 animate-pulse" />
                      <div className="h-2 w-full rounded bg-rgf-gray-100 animate-pulse" />
                    </li>
                  ))}
                </ul>
              ) : activityQuery.error ? (
                <div className="px-6 py-10 text-center space-y-3">
                  <p className="text-sm text-rgf-gray-600">
                    {(activityQuery.error as Error)?.message || 'Failed to load activity.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => activityQuery.refetch()}
                    className="text-sm font-medium text-rgf-primary-600 hover:text-rgf-primary-500"
                  >
                    Try again
                  </button>
                </div>
              ) : programBreakdown.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-rgf-gray-500">
                  No check-ins recorded today yet.
                </p>
              ) : (
                <ul className="divide-y divide-rgf-gray-100">
                  {programBreakdown.map(({ program, total, live }) => {
                    const widthPct = maxProgramTotal > 0
                      ? Math.max(4, Math.round((total / maxProgramTotal) * 100))
                      : 0;
                    return (
                      <li key={program.slug} className="px-6 py-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-rgf-gray-900 truncate">
                            {program.label}
                          </span>
                          <span className="text-rgf-gray-700 tabular-nums ml-3">
                            {total.toLocaleString()}
                            {live > 0 && (
                              <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-rgf-primary-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-rgf-primary-500 animate-pulse" />
                                {live} live
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-rgf-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-rgf-primary-500 rounded-full"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-100">
                <Link
                  to="/location-tracking/live"
                  className="text-sm font-medium text-rgf-primary-600 hover:text-rgf-primary-500"
                >
                  Open live tracking →
                </Link>
              </div>
            </div>
          </div>
        )}

        <QuickActionsCard roles={user?.roles} />
      </div>
    </div>
  );
};

// -------- adaptive layout helpers -----------------------------------

/**
 * Grid class for the stat-tile row. Sized to the number of tiles
 * actually rendered so the layout never leaves empty columns on
 * wider viewports.
 */
const tileGridClass = (count: number): string => {
  const baseClass = 'grid grid-cols-1 gap-5 sm:grid-cols-2';
  if (count <= 1) return `${baseClass} lg:grid-cols-1 max-w-sm`;
  if (count === 2) return `${baseClass} lg:grid-cols-2`;
  if (count === 3) return `${baseClass} lg:grid-cols-3`;
  return `${baseClass} lg:grid-cols-4`;
};

/**
 * Grid class for the card row. Never renders 4-up (visual noise);
 * caps at 3 cols. A single card gets a max-width so it doesn't
 * stretch across the whole viewport on desktop.
 */
const cardGridClass = (count: number): string => {
  const baseClass = 'grid grid-cols-1 gap-6';
  if (count <= 1) return `${baseClass} lg:grid-cols-1 max-w-2xl`;
  if (count === 2) return `${baseClass} lg:grid-cols-2`;
  return `${baseClass} lg:grid-cols-3`;
};

// -------- Quick Actions ---------------------------------------------

interface QuickActionSpec {
  key: string;
  to: string;
  title: string;
  body: string;
  icon: typeof Users;
  visible: boolean;
}

/**
 * Quick Actions card — a role-filtered set of links to the most-
 * used areas of the app. Each entry is gated on a role helper so
 * a viewer without permission for a section doesn't see a dead
 * link they can't follow. Falls back to a Welcome card when the
 * viewer's role set doesn't grant any accessible entry point.
 */
const QuickActionsCard = ({ roles }: { roles: string[] | undefined }) => {
  const actions: QuickActionSpec[] = [
    {
      key: 'users',
      to: '/users',
      title: 'Manage Users',
      body: 'Add, edit, or deactivate user accounts',
      icon: Users,
      visible: canReadUsers(roles),
    },
    {
      key: 'onboarding',
      to: '/onboarding/candidates',
      title: 'Onboarding',
      body: 'Review candidate cases and workflows',
      icon: Activity,
      visible: canOpenOnboarding(roles),
    },
    {
      key: 'honorarium',
      to: '/honorarium',
      title: 'Honorarium',
      body: 'Invoice review and approvals',
      icon: DollarSign,
      visible: canOpenHonorarium(roles),
    },
    {
      key: 'assets',
      to: '/asset-management/photo-sources',
      title: 'Asset Management',
      body: 'Impact photos, videos, and galleries',
      icon: ImageIcon,
      visible: canOpenAssets(roles),
    },
    {
      key: 'tracking',
      to: '/location-tracking/live',
      title: 'Live Tracking',
      body: 'Field team check-ins and movement',
      icon: MapPin,
      visible: canReadLiveTracking(roles),
    },
    {
      key: 'notifications',
      to: '/notifications/campaigns',
      title: 'Notifications',
      body: 'Campaigns, schedules, and device registry',
      icon: Bell,
      visible: canOpenNotifications(roles),
    },
  ].filter((a) => a.visible);

  if (actions.length === 0) {
    // The viewer has no role that maps to a dashboard section — say
    // so instead of dropping them onto an empty page.
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-rgf-gray-900">Welcome</h3>
        </div>
        <div className="card-body">
          <p className="text-sm text-rgf-gray-700">
            You're signed in, but your account doesn't have access to any
            dashboard sections yet.
          </p>
          <p className="text-sm text-rgf-gray-500 mt-2">
            If this is unexpected, ask an admin to grant you the roles you
            need — or reach out to the RGF backoffice team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-rgf-gray-900">Quick Actions</h3>
      </div>
      <div className="card-body">
        <div className="space-y-3">
          {actions.map((a, i) => {
            const Icon = a.icon;
            // Highlight only the first action per the original design;
            // subsequent rows use neutral chrome so the list stays
            // scannable even at six entries.
            const highlight = i === 0;
            return (
              <Link
                key={a.key}
                to={a.to}
                className={`block w-full text-left px-4 py-3 border rounded-lg transition-colors ${
                  highlight
                    ? 'bg-rgf-primary-50 border-rgf-primary-200 hover:bg-rgf-primary-100'
                    : 'bg-rgf-gray-50 border-rgf-gray-200 hover:bg-rgf-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <Icon
                    className={`h-5 w-5 mr-3 ${
                      highlight ? 'text-rgf-primary-600' : 'text-rgf-gray-600'
                    }`}
                  />
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        highlight ? 'text-rgf-primary-900' : 'text-rgf-gray-900'
                      }`}
                    >
                      {a.title}
                    </p>
                    <p
                      className={`text-xs ${
                        highlight ? 'text-rgf-primary-700' : 'text-rgf-gray-600'
                      }`}
                    >
                      {a.body}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
